import type { ForecastingRow } from '@/api/forecasting';
import type { BuyingPointRow, DrillLevel, DrillLevelConfig, KpiLens } from '../forecasting.types';
import { getMetricValue } from './forecastingAggregations';

export type DrillSelection = Partial<Record<DrillLevel, string>>;

export const DRILL_LEVELS: DrillLevelConfig[] = [
  { level: 'buyingPoint', title: 'Buying Point', field: 'Buying_Point', filterKey: 'buyingPoint' },
  { level: 'advertiser', title: 'Advertiser', field: 'Advertiser', filterKey: 'advertiser' },
  { level: 'channel', title: 'Channel', field: 'Channel', filterKey: 'channel' },
  { level: 'clientType', title: 'Client Type', field: 'Customer_Type', filterKey: 'clientType' },
  { level: 'newExisting', title: 'New / Existing', field: 'New_Existing', filterKey: 'newExisting' }
];

export function getDrillLevelConfig(levelIndex: number): DrillLevelConfig {
  const index = Math.min(Math.max(levelIndex, 0), DRILL_LEVELS.length - 1);
  return DRILL_LEVELS[index];
}

/** Label for rows with no value; not a real value, so it cannot be filtered on. */
export const UNKNOWN_DIMENSION = 'Unknown';

function rowDimensionValue(row: ForecastingRow, field: keyof ForecastingRow): string {
  const raw = row[field];
  if (raw == null || raw === '') return UNKNOWN_DIMENSION;
  return String(raw).trim() || UNKNOWN_DIMENSION;
}

export function filterRowsByDrillSelection(
  rows: ForecastingRow[],
  selection: DrillSelection,
  levelIndex: number
): ForecastingRow[] {
  const activeLevels = DRILL_LEVELS.slice(0, levelIndex + 1);
  const hasSelection = activeLevels.some((l) => selection[l.level] != null);
  if (!hasSelection) return rows;

  return rows.filter((row) =>
    activeLevels.every((level) => {
      const selected = selection[level.level];
      if (selected == null) return true;
      return rowDimensionValue(row, level.field) === selected;
    })
  );
}

export function drillSelectionLabel(selection: DrillSelection, levelIndex: number): string | null {
  const parts: string[] = [];
  for (let i = 0; i <= levelIndex; i++) {
    const level = DRILL_LEVELS[i];
    const value = selection[level.level];
    if (value) parts.push(`${level.title}: ${value}`);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

function aggregateByField(
  rows: ForecastingRow[],
  field: keyof ForecastingRow,
  lens?: KpiLens
): Map<string, { revenue: number; gp: number }> {
  const map = new Map<string, { revenue: number; gp: number }>();

  for (const row of rows) {
    const dim = rowDimensionValue(row, field);
    const cur = map.get(dim) ?? { revenue: 0, gp: 0 };
    cur.revenue += getMetricValue(row, 'revenue', lens);
    cur.gp += getMetricValue(row, 'gp', lens);
    map.set(dim, cur);
  }

  return map;
}

/** Forecast GP as a share of budget GP, the table's form of the "vs Budget" card. */
export function gpVsBudgetPercent(forecastGp: number, budgetGp: number): number | null {
  return budgetGp > 0 ? (forecastGp / budgetGp) * 100 : null;
}

export function groupDrilldownTable(
  rows: ForecastingRow[],
  levelIndex: number,
  lens?: KpiLens
): BuyingPointRow[] {
  const level = getDrillLevelConfig(levelIndex);
  const currentMap = aggregateByField(rows, level.field, lens);
  /* Against the dynamic forecast whichever card is picked: under a budget
     lens the GP column is the budget itself, and comparing it with itself
     would read 100% on every row. */
  const forecastMap = aggregateByField(rows, level.field);
  const budgetMap = aggregateByField(rows, level.field, 'budgetGp');

  const totalGp = [...currentMap.values()].reduce((s, v) => s + v.gp, 0);

  return [...currentMap.entries()]
    .map(([label, { revenue, gp }]) => ({
      key: `${level.level}:${label}`,
      label,
      buyingPoint: label,
      revenue,
      gp,
      gpSplit: totalGp > 0 ? (gp / totalGp) * 100 : 0,
      margin: revenue > 0 ? (gp / revenue) * 100 : 0,
      gpVsBudgetPct: gpVsBudgetPercent(forecastMap.get(label)?.gp ?? 0, budgetMap.get(label)?.gp ?? 0)
    }))
    .sort((a, b) => b.revenue - a.revenue);
}

export function canDrillDownLevel(levelIndex: number): boolean {
  return levelIndex < DRILL_LEVELS.length - 1;
}

export function canDrillUpLevel(levelIndex: number): boolean {
  return levelIndex > 0;
}

export function sumDrilldownTotals(
  rows: ForecastingRow[],
  lens?: KpiLens
): { revenue: number; gp: number } {
  let revenue = 0;
  let gp = 0;
  for (const row of rows) {
    revenue += getMetricValue(row, 'revenue', lens);
    gp += getMetricValue(row, 'gp', lens);
  }
  return { revenue, gp };
}
